import { useLayoutEffect, useState, type RefObject } from 'react';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { LIFECYCLE_STAGES } from './homeContent';

import type { HomeRouteMapHandle } from './HomeRouteMap';

gsap.registerPlugin(ScrollTrigger);

type LifecycleStageId = (typeof LIFECYCLE_STAGES)[number]['id'];
const STAGE_IDS: LifecycleStageId[] = LIFECYCLE_STAGES.map(s => s.id);

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function stageFromProgress(progress: number): LifecycleStageId {
  const idx = Math.min(
    STAGE_IDS.length - 1,
    Math.max(0, Math.floor(progress * STAGE_IDS.length))
  );
  return STAGE_IDS[idx] ?? 'observation';
}

export function useHomePageMotion(options: {
  rootRef: RefObject<HTMLElement | null>;
  routeMapRef: RefObject<HomeRouteMapHandle | null>;
  lifecycleSectionRef: RefObject<HTMLElement | null>;
  rolesRef: RefObject<HTMLElement | null>;
  suiteRef: RefObject<HTMLElement | null>;
  aiRef: RefObject<HTMLElement | null>;
}): { activeStage: LifecycleStageId } {
  const [activeStage, setActiveStage] = useState<LifecycleStageId>('observation');

  useLayoutEffect(() => {
    const root = options.rootRef.current;
    if (!root) return;

    if (prefersReducedMotion()) {
      return;
    }

    const pathEl = options.routeMapRef.current?.getPathElement();
    const activeOverlay = root.querySelector<SVGPathElement>(
      '[data-home-route-active]'
    );
    const stations = options.routeMapRef.current?.getStationElements() ?? [];
    const hero = root.querySelector('[data-home-hero]');
    const pillars = root.querySelectorAll('[data-home-pillar]');
    const lifecycleSection = options.lifecycleSectionRef.current;
    const stageDetail = root.querySelector('[data-home-stage-detail]');
    const stageTabs = root.querySelectorAll('[data-home-stage-tab]');

    const ctx = gsap.context(() => {
      const intro = gsap.timeline({ defaults: { ease: 'power3.out' } });

      if (hero) {
        gsap.set(hero.children, { opacity: 0, y: 28 });
        intro.to(
          hero.children,
          { opacity: 1, y: 0, duration: 0.65, stagger: 0.12 },
          0
        );
      }

      if (pathEl && activeOverlay) {
        const len = pathEl.getTotalLength();
        gsap.set(pathEl, {
          strokeDasharray: len,
          strokeDashoffset: len,
          opacity: 0.35,
        });
        gsap.set(activeOverlay, {
          strokeDasharray: len,
          strokeDashoffset: len,
        });
        gsap.set(stations, {
          scale: 0,
          opacity: 0,
          transformOrigin: 'center center',
        });

        intro.to(
          pathEl,
          { strokeDashoffset: 0, duration: 1.4, ease: 'power2.inOut' },
          0.15
        );
        intro.to(
          activeOverlay,
          { strokeDashoffset: len * 0.72, duration: 1.2, ease: 'power2.inOut' },
          0.35
        );
        intro.to(
          stations,
          {
            scale: 1,
            opacity: 1,
            duration: 0.45,
            stagger: 0.14,
            ease: 'back.out(1.6)',
          },
          0.55
        );
      }

      if (pillars.length) {
        gsap.set(pillars, { opacity: 0, y: 20 });
        intro.to(
          pillars,
          { opacity: 1, y: 0, duration: 0.5, stagger: 0.08 },
          0.45
        );
      }

      const routePanel = root.querySelector('[data-home-route-panel]');
      if (routePanel) {
        gsap.from(routePanel, {
          opacity: 0,
          x: 24,
          duration: 0.7,
          ease: 'power3.out',
          delay: 0.2,
        });
      }

      if (lifecycleSection && activeOverlay && pathEl) {
        const len = pathEl.getTotalLength();
        ScrollTrigger.create({
          trigger: lifecycleSection,
          start: 'top 70%',
          end: 'bottom 30%',
          scrub: 0.65,
          onUpdate: self => {
            const stage = stageFromProgress(self.progress);
            setActiveStage(stage);
            const draw = len * (1 - self.progress);
            gsap.set(activeOverlay, { strokeDashoffset: draw });
          },
        });
      }

      if (stageDetail) {
        gsap.from(stageDetail, {
          scrollTrigger: {
            trigger: stageDetail,
            start: 'top 88%',
            once: true,
          },
          opacity: 0,
          y: 16,
          duration: 0.55,
        });
      }

      if (stageTabs.length) {
        gsap.from(stageTabs, {
          scrollTrigger: {
            trigger: lifecycleSection,
            start: 'top 75%',
            once: true,
          },
          opacity: 0,
          x: 12,
          duration: 0.4,
          stagger: 0.08,
        });
      }

      const roleItems = options.rolesRef.current?.querySelectorAll(
        '[data-home-role]'
      );
      if (roleItems?.length) {
        gsap.from(roleItems, {
          scrollTrigger: {
            trigger: options.rolesRef.current,
            start: 'top 82%',
            once: true,
          },
          opacity: 0,
          y: 22,
          duration: 0.5,
          stagger: 0.07,
        });
      }

      const suiteItems = options.suiteRef.current?.querySelectorAll(
        '[data-home-suite-capability]'
      );
      if (suiteItems?.length) {
        gsap.from(suiteItems, {
          scrollTrigger: {
            trigger: options.suiteRef.current,
            start: 'top 85%',
            once: true,
          },
          opacity: 0,
          y: 18,
          duration: 0.45,
          stagger: 0.04,
        });
      }

      const aiSteps = options.aiRef.current?.querySelectorAll('[data-home-ai-step]');
      if (aiSteps?.length) {
        gsap.from(aiSteps, {
          scrollTrigger: {
            trigger: options.aiRef.current,
            start: 'top 85%',
            once: true,
          },
          opacity: 0,
          x: -16,
          duration: 0.45,
          stagger: 0.1,
        });
      }
    }, root);

    return () => ctx.revert();
  }, [
    options.rootRef,
    options.routeMapRef,
    options.lifecycleSectionRef,
    options.rolesRef,
    options.suiteRef,
    options.aiRef,
  ]);

  return { activeStage };
}
